import React, { createContext, useContext, ReactNode, useState, useEffect } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import SidebarComponent from '../Components/Navigation/SidebarComponent';

interface NavigationSubTab {
  label: string;
  icon: string;
  route: string;
  page: string;
}

interface NavigationTab {
  label: string;
  icon: string;
  route: string;
  page: string;
  subTabs: NavigationSubTab[] | null;
  department: string[],
  privileges: string[]
}

interface NavigationContextValue {
  sideNavigation: NavigationTab[];
  sideNavigationFollowup: NavigationTab[];
  sideNavigationClaims: NavigationTab[];
  topNavigation: NavigationTab[];
  currentSidebarTab: string;
  currentSidebarType: string;
  currentSidebarSubTab: string;
  currentContentTab: string;
  isMobileSidebarOpen: boolean;
  isMobile: boolean;
  isSidebarPinned: boolean;
  setSidebarPinned: (pinned: boolean) => void;
  handleUpdateCurrentSidebarTab: (text: string, path: string) => void;
  handleUpdateCurrentSidebarType: (text: string) => void;
  handleUpdateCurrentSidebarSubTab: (text: string) => void;
  handleUpdateCurrentContentTab: (text: string) => void;
  toggleMobileSidebar: () => void;
  closeMobileSidebar: () => void;
}

const NavigationContext = createContext<NavigationContextValue | undefined>(undefined);

export const NavigationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const navigate = useNavigate();

  const sideNavigationClaims = [
    {
      label: 'Claims',
      icon: 'clipboard',
      route: '/claims-data',
      page: 'table',
      subTabs: null,
      department: [
        'administration',
        'dev',
        'billing',
        'claims',
        'intake',
        'followup',
        'bd',
        ''
      ],
      privileges: [
        'staff',
        'admin',
        'dev',
        'owner'
      ]
    },
    {
      label: 'Claims Status',
      icon: 'file-text',
      route: '/claims',
      page: 'table',
      subTabs: null,
      department: [
        'administration',
        'dev',
        'billing',
        'claims',
        'intake',
        'followup',
        'bd',
        ''
      ],
      privileges: [
        'admin',
        'dev',
        'owner'
      ]
    },
    {
      label: 'Census',
      icon: 'grid',
      route: '/census',
      page: 'table',
      subTabs: null,
      department: [
        'administration',
        'dev',
        'claims',
        'followup'
      ],
      privileges: [
        'staff',
        'admin',
        'dev',
        'owner'
      ]
    }
  ]

  const sideNavigationFollowup = [
    {
      label: 'Claims',
      icon: 'clipboard',
      route: '/claims-data',
      page: 'table',
      subTabs: null,
      department: [
        'administration',
        'dev',
        'billing',
        'claims',
        'intake',
        'followup',
        'bd',
        ''
      ],
      privileges: [
        'staff',
        'admin',
        'dev',
        'owner'
      ]
    },
    {
      label: 'Claims Status',
      icon: 'file-text',
      route: '/claims',
      page: 'table',
      subTabs: null,
      department: [
        'administration',
        'dev',
        'billing',
        'claims',
        'intake',
        'followup',
        'bd',
        ''
      ],
      privileges: [
        'admin',
        'dev',
        'owner'
      ]
    },
    {
      label: 'Census',
      icon: 'grid',
      route: '/census',
      page: 'table',
      subTabs: null,
      department: [
        'administration',
        'dev',
        'claims',
        'followup'
      ],
      privileges: [
        'staff',
        'admin',
        'dev',
        'owner'
      ]
    },
  ]

  const sideNavigation = [
    {
      label: 'Dashboard',
      icon: 'home',
      route: '/intake-analytics',
      page: 'table',
      subTabs: null,
      department: [
        'intake',
        'administration',
        'admin',
        'dev',
        'bd'
      ],
      privileges: [
        'manager',
        'admin',
        'dev',
        'owner',
        'director'
      ]
    },
    {
      label: 'Intake',
      icon: 'phone',
      route: '/intake',
      page: 'table',
      subTabs: null,
      department: [
        'intake',
        'administration',
        'dev',
        'claims',
        'followup',
        'bd'
      ],
      privileges: [
        'staff',
        'manager',
        'admin',
        'dev',
        'owner',
        'director'
      ]
    },
    {
      label: 'VOB Rates',
      icon: 'dollar-sign',
      route: '/vob-rates',
      page: 'table',
      subTabs: null,
      department: [
        'administration',
        'dev',
        'intake'
      ],
      privileges: [
        'admin',
        'dev',
        'owner',
        'director'
      ]
    },
    {
      label: 'Census',
      icon: 'grid',
      route: '/census',
      page: 'table',
      subTabs: null,
      department: [
        'administration',
        'dev',
        'claims',
        'followup'
      ],
      privileges: [
        'staff',
        'admin',
        'dev',
        'owner'
      ]
    },
    {
      label: 'Claims',
      icon: 'clipboard',
      route: '/claims-data',
      page: 'table',
      subTabs: null,
      department: [
        'administration',
        'dev',
        'billing',
        'claims',
        'intake',
        'followup',
        'bd',
        ''
      ],
      privileges: [
        'staff',
        'admin',
        'dev',
        'owner'
      ]
    },
    {
      label: 'Claims Status',
      icon: 'file-text',
      route: '/claims',
      page: 'table',
      subTabs: null,
      department: [
        'administration',
        'dev',
        'billing',
        'claims',
        'intake',
        'followup',
        'bd',
        ''
      ],
      privileges: [
        'admin',
        'dev',
        'owner'
      ]
    },
    {
      label: 'Intellachat AI',
      icon: 'zap',
      route: '/intellachat',
      page: 'table',
      subTabs: null,
      department: [
        'administration',
        'dev'
      ],
      privileges: [
        'admin',
        'dev',
        'owner'
      ]
    },
  ];

  const topNavigation = [
    {
      label: '',
      icon: 'bell',
      route: '',
      page: 'popup',
      subTabs: null,
      department: [
        'intake',
        'billing',
        'administration',
        'dev',
        'bd'
      ],
      privileges: [
        'staff',
        'manager',
        'admin',
        'dev',
        'owner'
      ]
    },
    {
      label: '',
      icon: 'help-circle',
      route: '/help',
      page: 'static',
      subTabs: null,
      department: [
        'intake',
        'billing',
        'administration',
        'dev',
        'bd'
      ],
      privileges: [
        'staff',
        'manager',
        'admin',
        'dev',
        'owner'
      ]
    },
    // {
    //   label: '',
    //   icon: 'settings',
    //   route: '/settings',
    //   page: 'static',
    //   subTabs: null,
    //   department: [
    //     'intake',
    //     'billing',
    //     'administration',
    //     'dev'
    //   ],
    //   privileges: [
    //     'staff',
    //     'manager',
    //     'admin',
    //     'dev',
    //     'owner'
    //   ]
      
    // },
    {
      label: '',
      icon: 'log-out',
      route: '/auth/logout',
      page: 'status',
      subTabs: null,
      department: [
        'intake',
        'billing',
        'administration',
        'dev',
        'bd'
      ],
      privileges: [
        'staff',
        'manager',
        'admin',
        'dev',
        'owner'
      ]
    },
  ];

  const {currentProfile} = useAuth()

  const [currentSidebarTab, setCurrentSidebarTab] = useState<string>('')
  const [currentSidebarType, setCurrentSidebarType] = useState<string>('table')
  const [currentSidebarSubTab, setCurrentSidebarSubTab] = useState<string>('Collab Md')
  const [currentContentTab, setCurrentContentTab] = useState<string>(localStorage.getItem('activeTab') || 'Home')
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState<boolean>(false)
  const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth < 768)
  const [isSidebarPinned, setIsSidebarPinned] = useState<boolean>(false)

  const setSidebarPinned = (pinned: boolean) => {
    setIsSidebarPinned(pinned)
  }

  // Handle responsive detection
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768
      setIsMobile(mobile)
      if (!mobile) {
        setIsMobileSidebarOpen(false)
      }
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const toggleMobileSidebar = () => {
    setIsMobileSidebarOpen(prev => !prev)
  }

  const closeMobileSidebar = () => {
    setIsMobileSidebarOpen(false)
  }


  // Set initial sidebar tab based on user role and department
  useEffect(() => {
    if (!currentProfile.userid) return;

    // Admin, owner, dev start on Dashboard
    if (currentProfile.privileges === 'admin' ||
        currentProfile.privileges === 'owner' ||
        currentProfile.privileges === 'dev') {
      setCurrentSidebarTab('Dashboard')
    }
    // Claims department starts on Claims Status
    else if (currentProfile.department === 'claims') {
      setCurrentSidebarTab('Claims Status')
    }
    // Followup department starts on Claims Status
    else if (currentProfile.department === 'followup') {
      setCurrentSidebarTab('Claims Status')
    }
    // Intake department (staff/manager) starts on Intake
    else if (currentProfile.department === 'intake') {
      setCurrentSidebarTab('Intake')
    }
    // Default to Intake
    else {
      setCurrentSidebarTab('Intake')
    }
  }, [currentProfile])

  const handleUpdateCurrentSidebarTab = (text: string, path: string) => {
    localStorage.setItem('lastClickedRoute', path);
    localStorage.setItem('activeTab', text);
    localStorage.setItem('activePath', path);
    setCurrentSidebarTab(text)
    const currentPath = sideNavigation.find(tab => tab.label === text)
    if(currentPath){
      navigate(currentPath.route)
    } else {
      navigate('/intake')
    }
  }

  const handleUpdateCurrentSidebarType = (text: string) => {
    setCurrentSidebarType(text)
  }

  const handleUpdateCurrentSidebarSubTab = (text: string) => {
    setCurrentSidebarSubTab(text)
  }

  const handleUpdateCurrentContentTab = (text: string) => {
    setCurrentContentTab(text)
  }


  const value: NavigationContextValue = {
                                          sideNavigation,
                                          sideNavigationFollowup,
                                          sideNavigationClaims,
                                          topNavigation,
                                          currentSidebarTab,
                                          currentSidebarType,
                                          currentSidebarSubTab,
                                          currentContentTab,
                                          isMobileSidebarOpen,
                                          isMobile,
                                          isSidebarPinned,
                                          setSidebarPinned,
                                          handleUpdateCurrentSidebarTab,
                                          handleUpdateCurrentSidebarType,
                                          handleUpdateCurrentSidebarSubTab,
                                          handleUpdateCurrentContentTab,
                                          toggleMobileSidebar,
                                          closeMobileSidebar
                                         };

  return <NavigationContext.Provider value={value}>{children}</NavigationContext.Provider>;
};

export const useNavigation = () => {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error('useNavigation must be used within a NavigationProvider');
  }
  return context;
};